import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
//import HCaptcha from "@hcaptcha/react-hcaptcha";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthForm from "../../components/auth/AuthForm";
import FormField from "../../components/auth/FormField";
import PasswordInput from "../../components/auth/PasswordInput";
import Checkbox from "../../components/form/input/Checkbox";
import { supabase } from "../../integrations/supabase/client";
import { userStateService } from "../../services/UserStateService";

export default function SignUp() {
  const navigate = useNavigate();
  const fnameInputRef = useRef<HTMLInputElement>(null);
  const lnameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  //const hcaptchaRef = useRef<HCaptcha>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  //const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaToken = true;
  
  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        console.log('User is already logged in');
        navigate('/');
      }
    });
  }, [navigate]);

  const [isChecked, setIsChecked] = useState(false);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    // Get input values
    const firstName = fnameInputRef.current?.value || '';
    const lastName = lnameInputRef.current?.value || '';
    const email = emailInputRef.current?.value || '';
    const password = passwordInputRef.current?.value || '';
    const confirmPassword = confirmPasswordInputRef.current?.value || '';
    const fullName = `${firstName} ${lastName}`.trim();

    // Validate form
    if (!password) {
      setErrorMessage('Password is required');
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setPasswordsMatch(false);
      setIsLoading(false);
      return;
    }

    if (!isChecked) {
      setErrorMessage('You must agree to the Terms and Conditions');
      setIsLoading(false);
      return;
    }

    // if (!captchaToken) {
    //   setErrorMessage('Please complete the captcha verification');
    //   setIsLoading(false);
    //   return;
    // }

    try {
      const emailRedirectTo = `${window.location.origin}/signin`;
      //console.log("emailRedirectTo to:", emailRedirectTo);
      // Sign up with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          //captchaToken,
          emailRedirectTo,
          data: {
            name: fullName,
            first_name: firstName,
            last_name: lastName
          }
        }
      });

      if (error) {
        setErrorMessage(error.message);
      } else if (data && data.user) {
        // Set user info in UserStateService
        userStateService.setUserInfo(
          data.user.id,
          fullName,
          email
        );

        // // Reset captcha
        // hcaptchaRef.current?.resetCaptcha();
        // setCaptchaToken(null);

        // Navigate to sign in page with verification message
        navigate('/signin', { 
          state: { 
            verificationEmail: email,
            message: "Your account has been created successfully! Please check your email to verify your account before signing in.",
            status: "success"
          } 
        });
      }
    } catch (err) {
      console.error('Signup error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const checkPasswordsMatch = () => {
    const password = passwordInputRef.current?.value || '';
    const confirmPassword = confirmPasswordInputRef.current?.value || '';
    
    if (confirmPassword && password !== confirmPassword) {
      setPasswordsMatch(false);
    } else {
      setPasswordsMatch(true);
    }
  };

  return (
    <AuthLayout
      title="Sign Up"
      subtitle="Enter your email and password to sign up (no credit card required)"
      metaTitle="Sign Up | PDF ConvertOne"
      metaDescription="Create an account with PDF ConvertOne to convert your bank statement PDF files to CSV"
    >
      <AuthForm
        onSubmit={handleSignUp}
        errorMessage={errorMessage}
        bottomContent={
          <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
            Already have an account?{" "}
            <Link
              to="/signin"
              className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
            >
              Sign In
            </Link>
          </p>
        }
      >
        <FormField
          label="Email"
          type="email"
          placeholder="Enter your email"
          ref={emailInputRef}
          required
        />
        
        <PasswordInput
          label="Password"
          placeholder="Enter your password"
          ref={passwordInputRef}
          required
          minLength={6}
          onChange={checkPasswordsMatch}
        />
        
        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm your password"
          ref={confirmPasswordInputRef}
          required
          minLength={6}
          onChange={checkPasswordsMatch}
          className={!passwordsMatch ? "border-error-500 focus:border-error-500" : ""}
          errorMessage={!passwordsMatch ? "Passwords do not match" : undefined}
        />
        
        <div className="flex items-center gap-3">
          <Checkbox
            className="w-5 h-5"
            checked={isChecked}
            onChange={setIsChecked}
          />
          <p className="inline-block font-normal text-gray-500 dark:text-gray-400">
            By creating an account means you agree to the{" "}
            <span className="text-gray-800 dark:text-white/90">
              <a href="https://www.pdfconvertone.com/terms" target="_blank" rel="noopener noreferrer">
                Terms and Conditions,
              </a>
            </span>{" "}
            and our{" "}
            <span className="text-gray-800 dark:text-white">
              <a href="https://www.pdfconvertone.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </span>
          </p>
        </div>

        {/* <div className="flex justify-center">
          <HCaptcha
            ref={hcaptchaRef}
            sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY || ''}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
          />
        </div> */}
        
        <div>
          <button
            type="submit"
            disabled={isLoading || !passwordsMatch || !captchaToken}
            className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600 disabled:bg-brand-400 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing Up...' : 'Sign Up'}
          </button>
        </div>
      </AuthForm>
    </AuthLayout>
  );
}
